import { useEffect } from "react";
import {
  claimOncePerSession,
  sessionScopedEventId,
} from "../tools/analytics/eventIdentity";

/**
 * The census of what this hook counts. A closed union and not a free string
 * because the fire-once scope is now the SESSION and not the mount, so two call
 * sites that share a key namespace silence each other: `room.id` identifies a
 * room on Explore and the same room on the feed, which are two impressions of
 * two different cards.
 */
type TrackOnMountEvent =
  | "announcement-shown"
  | "desktop-auth-gate-shown"
  | "gpt-anonymous-refusal-shown"
  | "gpt-answer-sources-shown"
  | "gpt-room-suggestion-shown"
  | "install-wall-shown"
  | "juiscigpt-wizard-invitation-shown"
  | "partner-objection-shown"
  | "gpt-visual-abstract-offer-shown"
  | "practice-collections-viewed"
  | "practice-profiling-gate-shown"
  | "practice-question-shown"
  | "practice-rail-viewed"
  | "profile-viewed"
  | "push-opt-in-shown"
  | "room-consent-shown"
  | "room-recommendation-shown"
  | "room-welcome-shown"
  | "visual-abstract-library-offer-shown"
  | "visual-abstract-library-refusal-shown";

/**
 * What the hook is looking at. `shown` carries the identity of the thing on
 * screen; `hidden` is every reason there is nothing to count — the data has not
 * arrived, the modal is closed, the reader left.
 *
 * A union and not a nullable key because those states were being smuggled
 * through sentinel key strings (`"hidden"`, `"no-specialty"`) with a matching
 * early return inside the callback, which §7 forbids and which made a "do not
 * fire" indistinguishable from an identity.
 */
type TrackOnMountTarget =
  { status: "shown"; key: string } | { status: "hidden" };

/**
 * Fire an analytics event once per SESSION per (event, key), when the thing it
 * describes appears on screen.
 *
 * Impressions are the one side effect that genuinely has no user action to hang
 * off: "the card was shown" is the denominator of every conversion rate, and it
 * happens whether or not the user does anything. This hook is the sanctioned
 * home for that `useEffect` (see the webapp React rules) — business components
 * call it instead of growing an effect of their own.
 *
 * The scope used to be the mount, held in a `useRef`, and a `useRef` does not
 * survive a remount. Every reader who opened a card and came back was counted
 * again, so the interaction rate of the surface they came back to could never
 * exceed roughly 50%. Read the pair as session-level counts, never as an
 * ordered sequence: the numerator is "opens in this session", the denominator
 * "sessions that saw it", and neither moves when the reader bounces in and out.
 */
export function useTrackOnMount(
  event: TrackOnMountEvent,
  target: TrackOnMountTarget,
  /**
   * `sourceEventId` fingerprints the same (event, key, session) the claim was
   * made on, for the callbacks that write an operational row and not only a GA
   * event. Handed down rather than re-derived, so the event name is spelled in
   * the union above and nowhere else.
   */
  track: (claim: { key: string; sourceEventId: string }) => void
): void {
  const key = target.status === "shown" ? target.key : null;

  useEffect(() => {
    if (key === null) return;
    const fact = `${event}:${key}`;
    if (!claimOncePerSession(fact)) return;
    track({ key, sourceEventId: sessionScopedEventId(fact) });
  }, [event, key, track]);
}
